import { useTheme, useMediaQuery } from "@mui/material";

export interface BreakpointInfo {
  isMobile: boolean; // xs: 0-599px
  isTablet: boolean; // sm: 600-899px
  isDesktop: boolean; // md+: 900px+
  isLargeDesktop: boolean; // lg+: 1200px+
  isExtraLarge: boolean; // xl: 1536px+
  breakpoint: "xs" | "sm" | "md" | "lg" | "xl";
}

/**
 * Custom hook for responsive breakpoint detection.
 * Uses MUI's breakpoint system for consistency.
 *
 * Breakpoints:
 * - xs: 0-599px (mobile)
 * - sm: 600-899px (tablet)
 * - md: 900-1199px (desktop)
 * - lg: 1200-1535px (large desktop)
 * - xl: 1536px+ (extra large)
 *
 * NOTE: A canonical copy now lives in @insight/design-system so news-ui
 * can share the same semantics. When packages/ui is migrated to consume
 * design-system (task #27), this file should switch to a re-export and
 * the duplicate can be removed.
 */
export const useBreakpoint = (): BreakpointInfo => {
  const theme = useTheme();

  // Use MUI's useMediaQuery for each breakpoint
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));
  const isXl = useMediaQuery(theme.breakpoints.only("xl"));

  // Determine current breakpoint
  const breakpoint: BreakpointInfo["breakpoint"] = isXl
    ? "xl"
    : isLg
      ? "lg"
      : isMd
        ? "md"
        : isSm
          ? "sm"
          : "xs";

  return {
    isMobile: isXs,
    isTablet: isSm,
    isDesktop: useMediaQuery(theme.breakpoints.up("md")),
    isLargeDesktop: useMediaQuery(theme.breakpoints.up("lg")),
    isExtraLarge: isXl,
    breakpoint,
  };
};

export default useBreakpoint;
